import { AppDispatch, useAppDispatch, useAppSelector } from 'app/store';
import { fetchAudioForPlayingEffect } from 'app/store/AudioPlayer/effects';
import { AudioPlayerAudioInstance } from 'app/store/AudioPlayer/slice.types';
import axios from 'axios';
import WaveformData from 'waveform-data';
import { addAlertAction } from 'app/store/Alerts/slice';
import { getAudioPlayer } from 'app/store/AudioPlayer/selectors';
import {
  resetCurrentAudioErrorStateAction,
  setIsPlayerReadyAction,
  setIsPlayingAction,
  setIsWaveformError,
  setIsWaveformOpen,
  setPlaylistLoadingStateAction,
  setPlayOnInitialLoadAction,
  togglePlayerOpenAction,
} from 'app/store/AudioPlayer/slice';
import { useCallback, useEffect, useRef } from 'react';
import { CustomFetchAudioForPlayingEffect } from 'app/store/Shares/slice.types';
import { PlaybackEngine } from './playbackEngine.types';
import { useMidiEngine } from './useMidiEngine';
import { useWaveSurferEngine } from './useWaveSurferEngine';

const TARGETED_POINT_COUNT = 150;
const SCALE_POINT = 0.1;

export const loadWaveformData = async (dispatch: AppDispatch, src?: string) => {
  dispatch(setIsWaveformError(false));
  if (!src) {
    return null;
  }
  try {
    const { data } = await axios.get(src, {
      responseType: 'arraybuffer',
    });
    const wf = await WaveformData.create(data);
    return wf
      .channel(0)
      .max_array()
      .filter((peak) => peak >= 0);
  } catch (e: any) {
    dispatch(setIsWaveformError(true));
    dispatch(setIsWaveformOpen(false));
    dispatch(
      addAlertAction({
        message: 'Unable to load waveform. Please try again later',
      })
    );
    return null;
  }
};

const getReducedPeaks = (peaks: number[]) => {
  const reduceBy = Math.max(Math.floor(peaks.length / TARGETED_POINT_COUNT), 1);
  const reducedWaveform = peaks.filter(
    (_, index) => (index + 1) % reduceBy === 0
  );

  const smaller = reducedWaveform.map((peak) => Math.floor(peak * SCALE_POINT));
  return [reducedWaveform, smaller];
};

export const useAudio = ({
  customFetchAudioForPlayingEffect,
}: {
  customFetchAudioForPlayingEffect?: CustomFetchAudioForPlayingEffect;
}): { playbackEngine: PlaybackEngine } => {
  const wavesurferEngine = useWaveSurferEngine();
  const midiEngine = useMidiEngine();

  const {
    queue,
    queueIndex,
    isPlaying,
    playOnInitialLoad,
    playMode,
  } = useAppSelector(getAudioPlayer);
  const currentAudio = queue[queueIndex];
  const isMidi = currentAudio?.isMidi || false;
  const playbackEngine: PlaybackEngine = isMidi ? midiEngine : wavesurferEngine;

  const dispatch = useAppDispatch();
  const latestCurrentAudio = useRef(currentAudio);
  const latestPlayOnInitialLoad = useRef(playOnInitialLoad);

  const handleAudioFetch = useCallback(
    async (audioToFetch: AudioPlayerAudioInstance) => {
      if (!audioToFetch || audioToFetch?.isError) {
        return;
      }
      const shouldFetchAudio =
        !audioToFetch?.audioSrc && !audioToFetch?.loading;

      if (shouldFetchAudio) {
        wavesurferEngine.stop();
        midiEngine.stop();

        const fetchEffect =
          customFetchAudioForPlayingEffect || fetchAudioForPlayingEffect;
        await dispatch(fetchEffect(audioToFetch));
      }
    },
    [dispatch, customFetchAudioForPlayingEffect, wavesurferEngine, midiEngine]
  );

  const handleAudioLoadAndPlay = useCallback(
    async (audioToLoad: AudioPlayerAudioInstance) => {
      if (!audioToLoad || !audioToLoad?.audioSrc) {
        return;
      }
      dispatch(setIsPlayerReadyAction(false));

      const { audioSrc, waveformSrc } = audioToLoad;

      if (audioToLoad.isMidi) {
        try {
          wavesurferEngine.stop();
          midiEngine.stop();
          await midiEngine.loadMidi(audioSrc);
          dispatch(setIsPlayerReadyAction(true));

          if (!latestPlayOnInitialLoad.current) {
            dispatch(setPlayOnInitialLoadAction(true));
            return;
          }

          dispatch(setIsPlayingAction(true));
          dispatch(togglePlayerOpenAction(true));
          const isTheAudioStillTheSame =
            audioSrc === latestCurrentAudio.current?.audioSrc;
          if (audioSrc && isTheAudioStillTheSame) {
            await midiEngine.play();
          }
        } catch {
          dispatch(setIsPlayerReadyAction(true));
          dispatch(
            addAlertAction({
              message: 'Unable to load MIDI file. Please try again later',
            })
          );
        }
        return;
      }

      midiEngine.stop();

      const waveform = await loadWaveformData(dispatch, waveformSrc);
      const peaks = getReducedPeaks(waveform || []);
      await window.player?.load(audioSrc, peaks);

      if (!latestPlayOnInitialLoad.current) {
        dispatch(setPlayOnInitialLoadAction(true));
        return;
      }

      dispatch(setIsPlayingAction(true));
      dispatch(togglePlayerOpenAction(true));
      const isTheAudioStillTheSame =
        audioSrc === latestCurrentAudio.current?.audioSrc;
      if (audioSrc && isTheAudioStillTheSame) {
        window.player?.play().catch(() => {
          // proceed
        });
      }
    },
    [dispatch, midiEngine, wavesurferEngine]
  );

  useEffect(() => {
    handleAudioFetch(currentAudio);
    latestCurrentAudio.current = currentAudio;
  }, [currentAudio, handleAudioFetch]);

  useEffect(() => {
    latestPlayOnInitialLoad.current = playOnInitialLoad;
  }, [playOnInitialLoad]);

  useEffect(() => {
    handleAudioLoadAndPlay(currentAudio);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAudio?.audioSrc, handleAudioLoadAndPlay]);

  useEffect(() => {
    if (isPlaying && currentAudio?.isError) {
      dispatch(resetCurrentAudioErrorStateAction());
    }
  }, [dispatch, isPlaying, currentAudio?.isError]);

  useEffect(() => {
    if (playMode !== 'playlist') {
      dispatch(
        setPlaylistLoadingStateAction({
          loadingPlaylistGuid: undefined,
          loadingPlaylist: undefined,
        })
      );
    }
  }, [playMode, dispatch]);

  return { playbackEngine };
};
